import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Text,
  Image,
  useColorModeValue,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Center,
  Flex,
  Badge,
  VStack,
  HStack,
  CircularProgress,
  CircularProgressLabel,
  Skeleton,
  SkeletonText
} from '@chakra-ui/react';
import Loader from '../../components/Loader';
// import { useGetRewardQrCodeMutation } from '../../slices/userApiSlice';
import { useGetRewardQrCodeBatchMutation } from '../../slices/userApiSlice';
import { useTranslation } from 'react-i18next';

const Reward = ({ rewards, userRewards, isLoading, onModalClose }) => {
  // Theme Colors
  const textColorCompleted = useColorModeValue('gray.50', 'gray.800');
  const textColorCompletedDark = useColorModeValue('gray.800', 'gray.200');
  const modalHeaderBadgeBgColor = useColorModeValue('#F4FDFF', 'gray.700');
  const modalHeaderTextColor = useColorModeValue('custom.900', 'gray.700');
  const bgColor = useColorModeValue('custom.900', 'gray.700');
  const textColor = useColorModeValue('custom.800', 'gray.50');
  const bgColorCompleted = useColorModeValue('teal.500', 'teal.300');
  const {t} = useTranslation();

  // Modal and QR Code State
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [qrCodeUrls, setQrCodeUrls] = useState({});
  const [selectedReward, setSelectedReward] = useState(null);
  const [isQRCodeLoading, setIsQRCodeLoading] = useState(false);
  const [getRewardQrCodeBatch] = useGetRewardQrCodeBatchMutation();
  const [imageLoaded, setImageLoaded] = useState(false);
  // For QR code preview modal
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const [previewImage, setPreviewImage] = useState('');

  const handleImageClick = (qrCodeUrl) => {
    setPreviewImage(qrCodeUrl);
    onPreviewOpen();
  };

  const handleImageLoad = () => {
    setImageLoaded(true); // Set the image loaded state to true once the image is fetched
  };

  useEffect(() => {
    if (!Array.isArray(rewards) || rewards.length === 0 || !userRewards?._id) return;
  
    const fetchQRCodeUrls = async () => {
      setIsQRCodeLoading(true);
  
      try {
        // Prepare the payload for the batch request
        const payload = rewards.map((reward) => ({
          rewardId: reward._id,
          userId: userRewards._id,
          pointsRequired: reward.points_required,
        }));
  
  
        // Make a single batch request
        const response = await getRewardQrCodeBatch(payload).unwrap();
  
        // Map the response to the reward IDs
        const urls = response.reduce((acc, qrCode) => {
          acc[qrCode.rewardId] = qrCode.url;
          return acc;
        }, {});
  
        setQrCodeUrls(urls);
      } catch (error) {
        console.error('Error fetching QR codes:', error);
      } finally {
        setIsQRCodeLoading(false);
      }
    };
  
    fetchQRCodeUrls();
  }, [rewards, userRewards, getRewardQrCodeBatch]);

  // Handle Redeem Click
  const handleRedeemClick = (reward) => {
    setSelectedReward(reward);
    onOpen();
  };

  // Reward Progress Component
  const RewardProgress = ({ user, reward }) => {
    const progressValue = (user?.current_points / reward.points_required) * 100;
    const isCompleted = user?.current_points >= reward.points_required;

    return (
      <CircularProgress
        my={2}
        bg={isCompleted ? bgColorCompleted : bgColor}
        borderRadius="full"
        value={progressValue}
        color={bgColorCompleted}
        size="60px" // Reduced size from 70px to 60px
        trackColor={isCompleted ? 'teal.500' : 'gray.200'}
      >
        <CircularProgressLabel
          color={isCompleted ? textColorCompleted : textColorCompletedDark}
          fontWeight="bold"
          fontSize="lg" // Adjusted font size to match the smaller size
        >
          {reward.points_required}
        </CircularProgressLabel>
      </CircularProgress>
    );
  };

  const RewardItem = ({ reward }) => (
    <Box
      key={reward._id}
      p={2}
      borderBottomWidth={2}
      bg={bgColor}
      color={textColor}
      borderRadius="xl"
      w="full"
    >
      <HStack spacing={2} align="center" justify="space-between">
        {/* Skeleton for Reward Image */}
        <Box position="relative" width="100px" height="60px">
          {!imageLoaded && (
            <Skeleton
              width="100px"
              height="60px"
              borderRadius="xl"
              position="absolute"
              top={0}
              left={0}
            />
          )}

          <Image
            src={reward.image_url || "/default_reward.png"}
            width="100px"
            height="60px"
            alt={reward.title}
            borderRadius="xl"
            objectFit="cover"
            flexShrink={0}
            onLoad={handleImageLoad}
            opacity={imageLoaded ? 1 : 0}
            position="absolute"
            top={0}
            left={0}
            onError={(e) => e.target.src = "/default_reward.png"}
            //fallbackSrc="/default_reward.png" // This ensures fallback without onError
          />
        </Box>

        {/* Skeleton for Reward Details */}
        <Box flex="1" minWidth="0">
          <SkeletonText
            noOfLines={2}
            spacing="2"
            skeletonHeight="4"
            isLoaded={imageLoaded} // Show skeleton only when image is not loaded
          >
            <Text fontWeight="bold" fontSize="sm" mb={1} noOfLines={2}>
              {reward.title}
            </Text>
            <Text fontSize="xs" color="gray.500" noOfLines={3}>
              {reward.description}
            </Text>
          </SkeletonText>
        </Box>

        {/* Skeleton for Reward Progress & Redeem Action */}
        <Box mb={5} alignSelf="center">
          <Skeleton
            isLoaded={imageLoaded} // Show skeleton only when image is loaded
            borderRadius="full" // Keeps it rectangular
            width="55px"
            height="55px"
          >
            <div
              onClick={
                userRewards?.current_points >= reward?.points_required
                  ? () => handleRedeemClick(reward)
                  : undefined
              }
              style={{
                cursor: userRewards?.current_points >= reward?.points_required ? "pointer" : "default",
              }}
            >
              <RewardProgress user={userRewards} reward={reward} />
            </div>
          </Skeleton>
        </Box>
      </HStack>
  </Box>
);

  // Reward Modal Component
  const RewardModal = () => (
     <Modal 
      isOpen={isOpen}
      onClose={() => {
        onClose(); // Close the modal
        onModalClose(); // Notify parent to refetch data
      }}
      size="sm" 
      borderRadius="2xl"
      >
      <ModalOverlay />
      <ModalContent>
        {/* Modal Header */}
        <ModalHeader
          boxShadow="lg"
          borderBottomWidth={2}
          color={modalHeaderTextColor}
          bgColor={bgColorCompleted}
        >
          <ModalCloseButton/>
          <Flex justify="space-between" mr={5} align="center">
            {/* Skeleton for Reward Title */}
            <Skeleton isLoaded={!!selectedReward} width="70%">
              <Text fontSize="lg" fontWeight="bold" isTruncated>
                {selectedReward?.title}
              </Text>
            </Skeleton>

            {/* Skeleton for Points Badge */}
            <Skeleton isLoaded={!!selectedReward} borderRadius="full">
              <Badge
                bg={modalHeaderBadgeBgColor}
                color={textColor}
                borderRadius="full"
                px="3"
                py="2"
                minW="60px"
                textAlign="center"
              >
                <Text fontSize="xl" fontWeight="bold">
                  {selectedReward?.points_required}
                </Text>
                <Text fontSize="xs" color={textColor} mb="2">
                  POINTS
                </Text>
              </Badge>
            </Skeleton>
          </Flex>
        </ModalHeader>

        {/* Modal Body */}
        <ModalBody p="5" justifyContent="center">
          {isQRCodeLoading ? (
            // Skeleton for QR Code Loading
            <Center>
              <Skeleton boxSize="170px" borderRadius="md" />
            </Center>
         ) : (
          <>
            {/* Skeleton for Terms and Conditions */}
            <SkeletonText
              isLoaded={!!selectedReward}
              noOfLines={2}
              spacing="2"
              skeletonHeight="4"
              mb="4"
            >
              <Text fontSize="md" fontWeight="bold" color={textColor} mb="2">
                {t('reward_modal.terms_title')}
              </Text>
              <Text fontSize="sm" color="gray.500" lineHeight="1.6" textAlign="left">
                {selectedReward?.description}
              </Text>
            </SkeletonText>
        
            {/* QR Code */}
            <Center p="6">
              <Image
                src={qrCodeUrls[selectedReward._id]}
                alt="Reward QR Code"
                boxSize="170px"
                cursor="pointer"
                onClick={() => handleImageClick(qrCodeUrls[selectedReward._id])}
                _hover={{ opacity: 0.8 }}
              />
            </Center>
        
            {/* Skeleton for QR Code Instruction */}
            <SkeletonText
              isLoaded={!!selectedReward}
              noOfLines={1}
              spacing="2"
              skeletonHeight="4"
            >
              <Text color={textColor} align="center" fontSize="sm" fontWeight="bold">
                {t('reward_modal.qr_instruction')}
              </Text>
            </SkeletonText>
          </>
        )}
        </ModalBody>
        
        {/* Modal Footer */}
        <ModalFooter>
          <Button
            width="100%"
            bg={bgColorCompleted}
            color={modalHeaderTextColor}
            onClick={() => {
              onClose();
              onModalClose();
            }}
          >
            {t('reward_modal.close_button')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );

  return (
    <VStack align="stretch" w="full" overflowY="auto" maxHeight="550px">
      {isLoading ? (
        <Loader />
      ) : rewards.length > 0 ? (
        rewards.map((reward) => <RewardItem key={reward._id} reward={reward} />)
      ) : (
        <Text color={textColor}>
          {t('no_data')}
        </Text>
      )}

      {/* Reward Modal */}
      {selectedReward && <RewardModal />}

      {/* QR Code Preview Modal */}
      <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} size="xs">
        <ModalOverlay />
        <ModalContent borderRadius="xl">
          <ModalBody display="flex" flexDirection="column" alignItems="center">
            <Box
              position="relative"
              width="300px"
              height="300px"
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
            <Image
              src={previewImage}
              alt="QR Code Preview"
              width="300px"
              height="300px"
              borderRadius="2xl"
            />
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default Reward;